import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Spin, Form, Input, Select, Switch, Modal, Space, Popconfirm, Tag, message } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/common/PageHeader';
import { ErrorResult } from '../../components/common/ErrorResult';
import { useTemplateDetail, useCreateTemplate, useUpdateTemplate, useDeleteTemplate, usePreviewTemplate } from '../../hooks/useSupport';
import type { AdminEmailTemplateCreateRequest, AdminEmailTemplateUpdateRequest } from '../../types/support';

const CATEGORY_OPTIONS = [
  { label: 'Welcome', value: 'welcome' },
  { label: 'Billing', value: 'billing' },
  { label: 'Support', value: 'support' },
  { label: 'Account', value: 'account' },
  { label: 'Notification', value: 'notification' },
  { label: 'Marketing', value: 'marketing' },
];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function EmailTemplateEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreateMode = !id;
  const [form] = Form.useForm();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [variableModalOpen, setVariableModalOpen] = useState(false);
  const [variableInputs, setVariableInputs] = useState<Record<string, string>>({});

  const { data: template, isLoading, isError, refetch } = useTemplateDetail(id ?? '');
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate(id ?? '');
  const deleteMutation = useDeleteTemplate();
  const previewMutation = usePreviewTemplate(id ?? '');

  if (!isCreateMode && isLoading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!isCreateMode && (isError || !template)) return <ErrorResult onRetry={refetch} />;

  const handleSave = (values: Record<string, unknown>) => {
    if (isCreateMode) {
      const payload: AdminEmailTemplateCreateRequest = {
        name: values.name as string,
        slug: values.slug as string,
        subject: values.subject as string,
        body_html: values.body_html as string,
        body_text: (values.body_text as string) || '',
        category: values.category as string | undefined,
        variables: values.variables_json ? JSON.parse(values.variables_json as string) : undefined,
      };
      createMutation.mutate(payload, {
        onSuccess: (data) => navigate(`/support/email-templates/${data.id}`),
        onError: (err: unknown) => {
          const msg = (err as { response?: { data?: { detail?: { error?: { message?: string } } } } })?.response?.data?.detail?.error?.message || 'Failed to create template';
          message.error(msg);
        },
      });
    } else {
      const payload: AdminEmailTemplateUpdateRequest = {};
      if (values.name !== template?.name) payload.name = values.name as string;
      if (values.subject !== template?.subject) payload.subject = values.subject as string;
      if (values.body_html !== template?.body_html) payload.body_html = values.body_html as string;
      if (values.body_text !== template?.body_text) payload.body_text = values.body_text as string;
      if (values.category !== template?.category) payload.category = values.category as string;
      if (values.is_active !== template?.is_active) payload.is_active = values.is_active as boolean;
      if (values.variables_json) {
        try {
          const parsed = JSON.parse(values.variables_json as string);
          payload.variables = parsed;
        } catch { /* ignore parse errors */ }
      }
      updateMutation.mutate(payload);
    }
  };

  const handleDelete = () => {
    if (!id) return;
    deleteMutation.mutate(id, {
      onSuccess: () => navigate('/support/email-templates'),
    });
  };

  const handlePreview = () => {
    if (!id || !template) return;
    // Extract variable names from template
    const varRegex = /\{\{\s*(\w+)\s*\}\}/g;
    const vars = new Set<string>();
    const fullText = `${template.subject} ${template.body_html} ${template.body_text || ''}`;
    let match;
    while ((match = varRegex.exec(fullText)) !== null) {
      vars.add(match[1]);
    }

    if (vars.size > 0) {
      const inputs: Record<string, string> = {};
      vars.forEach((v) => { inputs[v] = variableInputs[v] || `[${v}]`; });
      setVariableInputs(inputs);
      setVariableModalOpen(true);
    } else {
      executePreview({});
    }
  };

  const executePreview = (variables: Record<string, string>) => {
    previewMutation.mutate({ variables }, {
      onSuccess: (data) => {
        setPreviewSubject(data.subject);
        setPreviewHtml(data.body_html);
        setPreviewOpen(true);
        setVariableModalOpen(false);
      },
    });
  };

  const initialValues = isCreateMode
    ? { is_active: true, body_text: '', variables_json: '' }
    : {
        ...template,
        variables_json: template?.variables ? JSON.stringify(template.variables, null, 2) : '',
      };

  return (
    <>
      <PageHeader
        title={isCreateMode ? 'New Email Template' : template?.name ?? 'Edit Template'}
        extra={
          <Space>
            {!isCreateMode && (
              <>
                <Button icon={<EyeOutlined />} onClick={handlePreview} loading={previewMutation.isPending}>
                  Preview
                </Button>
                <Popconfirm
                  title="Delete this template?"
                  description="This action cannot be undone."
                  onConfirm={handleDelete}
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
                    Delete
                  </Button>
                </Popconfirm>
              </>
            )}
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/support/email-templates')}>Back</Button>
          </Space>
        }
      />
      <Card>
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={handleSave}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
              <Input
                onChange={(e) => {
                  if (isCreateMode) {
                    form.setFieldsValue({ slug: slugify(e.target.value) });
                  }
                }}
              />
            </Form.Item>
            <Form.Item name="slug" label="Slug" rules={[{ required: true, message: 'Slug is required' }, { pattern: /^[a-z0-9-]+$/, message: 'Lowercase letters, numbers, and hyphens only' }]}>
              <Input disabled={!isCreateMode} />
            </Form.Item>
          </div>

          <Form.Item name="subject" label="Subject" rules={[{ required: true, message: 'Subject is required' }]}>
            <Input placeholder="e.g. Welcome to {{app_name}}, {{user_name}}!" />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item name="category" label="Category">
              <Select allowClear placeholder="Select category" options={CATEGORY_OPTIONS} />
            </Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </div>

          <Form.Item name="body_html" label="HTML Body" rules={[{ required: true, message: 'HTML body is required' }]}>
            <Input.TextArea rows={12} style={{ fontFamily: 'monospace', fontSize: 12 }} placeholder="<h1>Hello {{user_name}}</h1>" />
          </Form.Item>

          <Form.Item name="body_text" label="Plain Text Body">
            <Input.TextArea rows={5} placeholder="Hello {{user_name}}" />
          </Form.Item>

          <Form.Item
            name="variables_json"
            label="Variables (JSON)"
            tooltip="Define available template variables as JSON object, e.g. {&quot;user_name&quot;: &quot;string&quot;, &quot;app_name&quot;: &quot;string&quot;}"
            rules={[{
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                try { JSON.parse(value); return Promise.resolve(); }
                catch { return Promise.reject('Invalid JSON'); }
              },
            }]}
          >
            <Input.TextArea rows={4} style={{ fontFamily: 'monospace', fontSize: 12 }} placeholder='{"user_name": "string", "app_name": "string"}' />
          </Form.Item>

          <Space>
            <Button type="primary" htmlType="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {isCreateMode ? 'Create Template' : 'Save Changes'}
            </Button>
            <Button onClick={() => navigate('/support/email-templates')}>Cancel</Button>
          </Space>
        </Form>
      </Card>

      {/* Variable input modal for preview */}
      <Modal
        title="Enter variable values for preview"
        open={variableModalOpen}
        onCancel={() => setVariableModalOpen(false)}
        onOk={() => executePreview(variableInputs)}
        confirmLoading={previewMutation.isPending}
      >
        {Object.keys(variableInputs).map((key) => (
          <div key={key} style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4 }}>
              <Tag>{`{{${key}}}`}</Tag>
            </label>
            <Input
              value={variableInputs[key]}
              onChange={(e) => setVariableInputs((prev) => ({ ...prev, [key]: e.target.value }))}
            />
          </div>
        ))}
      </Modal>

      {/* Preview modal */}
      <Modal
        title={`Preview: ${previewSubject}`}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={<Button onClick={() => setPreviewOpen(false)}>Close</Button>}
        width={800}
      >
        <div
          style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 24, background: '#fff', minHeight: 200 }}
          dangerouslySetInnerHTML={{ __html: previewHtml }}
        />
      </Modal>
    </>
  );
}
